import React from "react";
import { Card, CardContent, Typography, Divider, Box } from "@mui/material";
import { BusinessCenter, Event as EventIcon } from "@mui/icons-material";
import sharedStyles from "./sharedStyles";

interface Experience {
  employer: string;
  title: string;
  location: string;
  startDate: string;
  endDate: string;
  summary: string;
  responsibilities: string[];
  highlights: string[];
}

interface CandidateExperienceProps {
  experience: Experience[];
}

const CandidateExperience: React.FC<CandidateExperienceProps> = ({
  experience,
}) => {
  return (
    <Card sx={{ ...sharedStyles.card, backgroundColor: "#f8fafc" }}>
      <CardContent>
        <Box sx={sharedStyles.title}>
          <BusinessCenter sx={sharedStyles.smallIcon} />
          <Typography variant="h6" fontWeight="bold" data-testid="experience-title">
            Professional Experience
          </Typography>
        </Box>

        <Box
          sx={{
            ...sharedStyles.scrollableContainer,
            height: "427px",
          }}
          data-testid="experience-list"
        >
          {experience.map((job) => (
            <Box
              key={`${job.title}-${job.employer}-${job.startDate}`}
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: "white",
                borderRadius: 2,
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              }}
              data-testid={`experience-item-${job.title}-${job.employer}-${job.startDate}`}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: "bold",
                  color: "#1e293b",
                  fontSize: "1.1rem",
                }}
                data-testid={`experience-title-${job.title}-${job.employer}`}
              >
                {job.title}
              </Typography>
              
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#3b82f6",
                  fontWeight: "500",
                  mt: 1,
                }}
                data-testid={`experience-company-${job.employer}`}
              >
                {job.employer}
              </Typography>

              <Typography
                sx={{
                  ...sharedStyles.infoLabel,
                  display: "flex",
                  alignItems: "center",
                  mt: 1,
                }}
                data-testid={`experience-duration-${job.startDate}-${job.endDate}`}
              >
                <EventIcon 
                  sx={{ 
                    ...sharedStyles.smallIcon, 
                    mr: 1, 
                    fontSize: "1rem",
                    color: "#64748b" 
                  }} 
                />
                {job.startDate} – {job.endDate}
              </Typography>

              {job.location !== "N/A" && (
                <Typography
                  sx={{
                    ...sharedStyles.infoValue,
                    mt: 1,
                    color: "#64748b",
                  }}
                >
                  {job.location}
                </Typography>
              )}

              {job.summary !== "N/A" && (
                <Typography
                  sx={{
                    mt: 2,
                    color: "#475569",
                    fontSize: "0.875rem",
                    lineHeight: 1.5,
                  }}
                >
                  {job.summary}
                </Typography>
              )}

              {job.responsibilities.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{
                      fontWeight: "500",
                      color: "#1e293b",
                      mb: 1,
                    }}
                  >
                    Key Responsibilities:
                  </Typography>
                  {job.responsibilities.map((resp) => (
                    <Typography
                      key={resp}
                      sx={{
                        color: "#475569",
                        fontSize: "0.875rem",
                        mb: 0.5,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: "4px",
                          height: "4px",
                          backgroundColor: "#3b82f6",
                          borderRadius: "50%",
                          mr: 1,
                        }}
                      />
                      {resp}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default CandidateExperience;